/**
 * 牛只详情
 * 轮播 + 5 Tab（基础/溯源/在途/卖家/评价）+ sticky 交易面板
 * IR-13 卖家联系方式付款后解锁；竞价中隐藏一口价按钮
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCattle } from '@/api/cattle';
import { listAuctions } from '@/api/auction';
import { getTransitByCattle } from '@/api/transit';
import { listOrders } from '@/api/trade';
import { useAuth } from '@/stores/auth';
import type { Cattle, TraceEvent, TransitOrder } from '@/types';
import { GENDER_MAP, SALE_STATUS, TRADE_MODE, USER_TYPE } from '@/utils/constants';
import { formatDate, formatDateTime, formatMoney, formatWeight, maskPhone } from '@/utils/format';
import ImageCarousel from '@/components/common/ImageCarousel';
import StatusTag from '@/components/common/StatusTag';
import Loading from '@/components/common/Loading';
import ErrorBox from '@/components/common/ErrorBox';
import Empty from '@/components/common/Empty';
import '@/styles/pages/cattle.css';

const TABS = [
  { key: 'base', label: '基础信息' },
  { key: 'trace', label: '溯源档案' },
  { key: 'transit', label: '在途状态' },
  { key: 'seller', label: '卖家信息' },
  { key: 'review', label: '交易评价' },
];

export default function CattleDetail() {
  const { id } = useParams<{ id: string }>();
  const cattleId = Number(id);
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const [cattle, setCattle] = useState<Cattle | null>(null);
  const [trace, setTrace] = useState<TraceEvent[]>([]);
  const [transit, setTransit] = useState<TransitOrder | null>(null);
  const [auctionId, setAuctionId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState('base');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [contactUnlocked, setContactUnlocked] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [detail, transitData] = await Promise.all([
        getCattle(cattleId),
        getTransitByCattle(cattleId).catch(() => null),
      ]);
      setCattle(detail.cattle);
      setTrace(detail.traces ?? []);
      setTransit(transitData);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [cattleId]);

  useEffect(() => {
    load();
  }, [load]);

  // 竞价模式：按 cattle_id 找到对应竞价房间（进行中/预告）
  useEffect(() => {
    if (!cattle || cattle.trade_mode !== 2) return;
    listAuctions(1)
      .then((live) => {
        const hit = live.find((a) => a.cattle_id === cattle.id);
        if (hit) {
          setAuctionId(hit.id);
          return;
        }
        return listAuctions(0).then((preview) => {
          const p = preview.find((a) => a.cattle_id === cattle.id);
          setAuctionId(p?.id ?? null);
        });
      })
      .catch(() => setAuctionId(null));
  }, [cattle]);

  // IR-13：卖家联系方式付款后解锁（存在已付款订单含此牛只）
  useEffect(() => {
    if (!isAuthenticated) return;
    listOrders('buy', user?.id ?? 0)
      .then((orders) => {
        const paid = orders.some(
          (o) =>
            o.payment_status === 1 &&
            o.items?.some((it) => it.cattle_id === cattleId),
        );
        setContactUnlocked(paid);
      })
      .catch(() => {
        setContactUnlocked(false);
      });
  }, [cattleId, isAuthenticated]);

  const sellerName = useMemo(
    () => cattle?.seller?.enterprise_name || cattle?.seller?.real_name || cattle?.farm_name || '卖家',
    [cattle],
  );

  const onBuy = () => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent(`/checkout?cattle_id=${cattleId}`)}`);
      return;
    }
    navigate(`/checkout?cattle_id=${cattleId}`);
  };

  const onBid = () => {
    if (!auctionId) {
      navigate('/auctions');
      return;
    }
    if (!isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent(`/auction/${auctionId}`)}`);
      return;
    }
    navigate(`/auction/${auctionId}`);
  };

  if (loading && !cattle) {
    return (
      <div className="container cattle-page">
        <Loading rows={5} />
      </div>
    );
  }

  if (error && !cattle) {
    return (
      <div className="container cattle-page">
        <ErrorBox message={error} onRetry={load} />
      </div>
    );
  }

  if (!cattle) return null;

  const tradeMode = TRADE_MODE[cattle.trade_mode ?? 1] ?? TRADE_MODE[1];
  const saleStatus = SALE_STATUS[cattle.sale_status ?? 0];
  const onSale = (cattle.sale_status ?? 0) === 0;
  const isAuctionMode = cattle.trade_mode === 2;

  return (
    <div className="cattle-page container">
      <div className="cattle-detail">
        {/* 左：轮播 */}
        <div className="cattle-detail__media">
          <ImageCarousel
            images={cattle.images ?? (cattle.face_image_url ? [cattle.face_image_url] : [])}
            videoUrl={cattle.video_url}
            alt={cattle.breed}
            fallbackText={`${cattle.breed ?? '牛只'} · ${cattle.ear_tag_no}`}
          />
        </div>

        {/* 中：信息 + Tab */}
        <div className="cattle-detail__info">
          <div className="cattle-detail__head">
            <div className="cattle-detail__tags">
              <StatusTag type="trade" status={cattle.trade_mode ?? 1} />
              <StatusTag type="sale" status={cattle.sale_status ?? 0} />
              <span className="status-tag status-tag--default">{cattle.farm_name ?? '养殖场'}</span>
            </div>
            <h1 className="cattle-detail__title">
              {cattle.breed ?? '牛只'} · {cattle.ear_tag_no}
            </h1>
            <p className="cattle-detail__sub">
              耳标 {cattle.ear_tag_no} · RFID {cattle.rfid_tag ?? '-'} · 溯源星级{' '}
              {'★'.repeat(cattle.trace_score ?? 0)}
              {'☆'.repeat(5 - (cattle.trace_score ?? 0))}
            </p>
          </div>

          <div className="cattle-tabs tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                className={`tab-item ${activeTab === t.key ? 'active' : ''}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="cattle-tab-content">
            {activeTab === 'base' ? (
              <div className="cattle-info-grid">
                {[
                  ['品种', cattle.breed ?? '-'],
                  ['性别', GENDER_MAP[cattle.gender ?? 0] ?? '-'],
                  ['出生日期', formatDate(cattle.birth_date)],
                  ['体重', formatWeight(cattle.weight)],
                  ['日龄', cattle.age_days ? `${cattle.age_days} 天` : '-'],
                  ['毛色', cattle.coat_color ?? '-'],
                  ['养殖场', cattle.farm_name ?? '-'],
                  ['健康状态', cattle.health_status ?? '-'],
                  ['检疫证号', cattle.quarantine_cert ?? '-'],
                  ['可售数量', `${cattle.available_qty ?? 1} 头`],
                ].map(([k, v]) => (
                  <div className="cattle-info-item" key={k}>
                    <span className="cattle-info-item__label">{k}</span>
                    <span className="cattle-info-item__value">{v}</span>
                  </div>
                ))}
              </div>
            ) : null}

            {activeTab === 'trace' ? (
              trace.length > 0 ? (
                <div className="trace-timeline">
                  {trace.map((t) => (
                    <div className="trace-item" key={t.id}>
                      <div className="trace-item__dot" />
                      <div className="trace-item__body">
                        <div className="trace-item__head">
                          <strong>{t.event_desc ?? '溯源事件'}</strong>
                          <span>{formatDateTime(t.event_time)}</span>
                        </div>
                        <div className="trace-item__meta">
                          <span>📍 {t.location ?? '-'}</span>
                          <span>👤 {t.operator ?? '-'}</span>
                        </div>
                        {t.tx_hash ? (
                          <div className="trace-item__hash">
                            区块链存证 <code>{t.tx_hash}</code>
                            <span className="scan-link" title="链上查验平台即将上线，敬请期待">[链上查验·即将上线]</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty icon="🔗" title="暂无溯源记录" desc="该牛只溯源档案建设中" />
              )
            ) : null}

            {activeTab === 'transit' ? (
              transit ? (
                <div className="transit-block">
                  <div className="transit-block__head">
                    <strong>运输单 {transit.transit_no}</strong>
                    <StatusTag type="transit" status={transit.status} />
                  </div>
                  <div className="transit-block__rows">
                    <div><span>起运地</span><b>{transit.origin_city ?? '-'}</b></div>
                    <div><span>目的地</span><b>{transit.destination_city ?? '-'}</b></div>
                    <div><span>车牌号</span><b>{transit.plate_no ?? '-'}</b></div>
                    <div><span>司机</span><b>{transit.driver_name ?? '-'}（{maskPhone(transit.driver_phone)}）</b></div>
                    <div><span>起运时间</span><b>{formatDateTime(transit.depart_at)}</b></div>
                  </div>
                  {transit.gps_track && transit.gps_track.length > 0 ? (
                    <div className="transit-block__track">
                      <strong>GPS 轨迹（缩略）</strong>
                      <div className="transit-block__track-list">
                        {transit.gps_track.map((g, i) => (
                          <span key={i}>
                            {g.lat.toFixed(4)}, {g.lng.toFixed(4)} @ {formatDateTime(g.time)}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  <p className="transit-block__tip">查看态占位：完整在途监管请在独立在途监管平台查看</p>
                </div>
              ) : (
                <Empty
                  icon="🚚"
                  title="暂无运输信息"
                  desc="该牛只尚未生成在途运输单（查看态占位）"
                />
              )
            ) : null}

            {activeTab === 'seller' ? (
              <div className="seller-block">
                <div className="seller-block__head">
                  <span className="seller-block__avatar">{sellerName.slice(0, 1)}</span>
                  <div>
                    <strong>{sellerName}</strong>
                    <span className="seller-block__type">
                      {/* F2：有企业名称 → 企业；否则 → 散户 */}
                      {cattle.seller?.enterprise_name ? USER_TYPE[3] : USER_TYPE[4]}
                    </span>
                  </div>
                </div>
                <div className="seller-block__stats">
                  <div><b>{cattle.seller?.credit_score ?? '-'}</b><span>诚信分</span></div>
                  <div><b>128</b><span>成交量</span></div>
                  <div><b>4.9</b><span>好评率</span></div>
                </div>
                <div className="seller-block__contact">
                  {contactUnlocked ? (
                    <div className="seller-block__contact-open">
                      <p>联系方式（付款后解锁）：</p>
                      <p className="seller-block__phone">
                        电话 {maskPhone(cattle.seller ? '13800000000' : '13900000000')}
                      </p>
                    </div>
                  ) : (
                    <div className="seller-block__contact-lock">
                      🔒 卖家联系方式将在您付款后解锁（IR-13 数据规则）
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {activeTab === 'review' ? (
              <Empty icon="⭐" title="暂无评价" desc="交易完成后 15 天内可评价" />
            ) : null}
          </div>
        </div>

        {/* 右：sticky 交易面板 */}
        <aside className="cattle-panel">
          <div className="cattle-panel__price">
            <span className="cattle-panel__price-label">挂牌价</span>
            <span className="cattle-panel__price-value">
              {formatMoney(cattle.listing_price)}
              <em>/头</em>
            </span>
          </div>
          <div className="cattle-panel__meta">
            <span>{tradeMode.label}</span>
            <span>可售 {cattle.available_qty ?? 1} 头</span>
            <span>{saleStatus.label}</span>
          </div>

          {isAuctionMode ? (
            <>
              <div className="cattle-panel__notice">
                该牛只参与竞价（起拍价 {formatMoney(cattle.min_bid_price)}），暂不支持一口价
              </div>
              <button
                className="btn btn-gold btn-block btn-lg"
                disabled={!onSale}
                onClick={onBid}
              >
                {onSale ? '参与竞价' : '该牛只已不在售'}
              </button>
            </>
          ) : (
            <button
              className="btn btn-primary btn-block btn-lg"
              disabled={!onSale}
              onClick={onBuy}
            >
              {onSale ? '一口价购买' : '该牛只已不在售'}
            </button>
          )}

          <div className="cattle-panel__tips">
            <p>✓ 下单后锁定牛只 15 分钟</p>
            <p>✓ 资金进入平台监管账户</p>
            <p>✓ 交割确认需 ≥3 张凭证 + GPS</p>
            <p>✓ 合同 / 交割区块链存证</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
